/**
 * @file SeoKeywordsField.tsx
 * @description «Ключевые запросы» страницы, товара, категории и статьи
 * (phase19.16, партия Р3в, v0.19.64; SEO11, SEO12; контракт
 * r3v-api-contract §4): главный запрос и дополнительные чипами (до 20, по
 * 100 знаков). Заметка владельца — на сайт не выводится, мета-тега keywords
 * нет. Сведения (ⓘ), не блокировки: «главного запроса нет в заголовке для
 * поиска или в H1» — shared `checkKeywordInTitleOrH1` по текущим значениям
 * формы; «этот запрос уже у …» — ответ сервера (`seoKeywordConflicts`).
 *
 * Общий контракт `value/onChange`: поле встраивается в любую карточку, а
 * сохраняет его форма-владелец. Чип добавляется Enter, запятой или кнопкой
 * «Добавить»; удаляется ✕ (48 px).
 */

import { checkKeywordInTitleOrH1 } from "@chaiberi/shared";
import { Info, Plus, X } from "lucide-react";
import { useId, useState, type KeyboardEvent } from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { addKeyword, SEO_KEYWORD_MAX, SEO_KEYWORDS_EXTRA_MAX, type SeoKeywordConflict, type SeoKeywordsValue } from "../../lib/site-seo/keywords";

interface Props {
  value: SeoKeywordsValue;
  onChange: (value: SeoKeywordsValue) => void;
  /** Итоговый заголовок для поиска сейчас (для проверки SEO12). */
  title: string | null;
  /** Текст H1 (если известен). */
  h1?: string | readonly string[] | null;
  conflicts?: readonly SeoKeywordConflict[];
  disabled?: boolean;
  /** Как сохраняется: «сразу, вне публикации» у страницы. */
  note?: string;
}

export function SeoKeywordsField({ value, onChange, title, h1 = null, conflicts = [], disabled = false, note }: Props) {
  const id = useId();
  const [draft, setDraft] = useState("");
  const missing = checkKeywordInTitleOrH1({ primary: value.main, title, h1 });
  const full = value.extra.length >= SEO_KEYWORDS_EXTRA_MAX;

  function commit() {
    const next = addKeyword(value.extra, draft);
    if (next !== value.extra) onChange({ ...value, extra: next });
    setDraft("");
  }

  function onKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commit();
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-2" data-testid="seo-keywords">
      <div className="flex min-w-0 flex-col gap-1">
        <Label htmlFor={`${id}-main`}>Главный запрос</Label>
        <Input id={`${id}-main`} value={value.main} maxLength={SEO_KEYWORD_MAX} disabled={disabled} placeholder="иван-чай купить" onChange={(e) => onChange({ ...value, main: e.target.value })} />
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <Label htmlFor={`${id}-extra`}>Дополнительные запросы</Label>
        {value.extra.length > 0 ? (
          <ul className="flex flex-wrap gap-1" aria-label="Список дополнительных запросов">
            {value.extra.map((word) => (
              <li key={word} className="type-body flex min-w-0 max-w-full items-center rounded-full bg-surface-low pl-3 text-on-surface">
                <span className="min-w-0 truncate">{word}</span>
                <Button variant="ghost" size="icon" className="size-12 shrink-0 rounded-full lg:size-9" aria-label={`Убрать запрос «${word}»`} disabled={disabled} onClick={() => onChange({ ...value, extra: value.extra.filter((w) => w !== word) })}>
                  <X aria-hidden className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
        {full ? (
          <p className="type-caption text-on-surface-variant">Не больше {SEO_KEYWORDS_EXTRA_MAX} дополнительных запросов.</p>
        ) : (
          <div className="flex min-w-0 gap-2">
            <Input id={`${id}-extra`} className="min-w-0 flex-1" value={draft} maxLength={SEO_KEYWORD_MAX} disabled={disabled} placeholder="Запрос и Enter" onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} onBlur={() => draft.trim() && commit()} />
            <Button variant="outline" className="shrink-0" disabled={disabled || !draft.trim()} onClick={commit} aria-label="Добавить запрос">
              <Plus aria-hidden />
              <span className="max-sm:sr-only">Добавить</span>
            </Button>
          </div>
        )}
      </div>
      <p className="type-caption text-on-surface-variant">{note ?? "Заметка для вас: на сайт не выводится."}</p>
      {missing ? <KeywordInfo>Главного запроса нет в заголовке для поиска или в главном заголовке H1.</KeywordInfo> : null}
      {conflicts.length > 0 ? <KeywordInfo>Этот главный запрос уже у: {conflicts.map((c) => `«${c.title}»`).join(", ")} — страницы будут бороться за один запрос.</KeywordInfo> : null}
    </div>
  );
}

function KeywordInfo({ children }: { children: React.ReactNode }) {
  return (
    <p className="type-caption flex items-start gap-1.5 text-info">
      <Info aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      <span className="sr-only">Сведение: </span>
      <span>{children}</span>
    </p>
  );
}
